import { defaultCacheableSingleton } from '@/lib/cacheable';
import { hygraphClient } from '@/lib/graphql-clients';
import {
  getSdk,
  Page,
  Region,
  RowrowComponentsUnion,
  StaticComponentOptions,
} from '@/lib/types';

type TPopularDestination = {
  state: string;
  viewers: number;
  pay_rate: number;
  published_jobs: number;
};

import lookerQueryClient from '@/pages/api/api-clients/looker-query-api-client';
import { endOfDay } from '@/utils/date-utils';
import { getPageComponents } from '@/utils/page-static-utils';

export type PopularDestinationsResponse = {
  stats: TPopularDestination;
  region: Region;
}[];

type GetProps = {
  limit?: number;
  preview?: boolean;
};

const getPopularDestinations = async ({ limit, preview = false }: GetProps) => {
  return defaultCacheableSingleton.remember(
    'looker:popularDestinations',
    async () => {
      const popularDestinations = await lookerQueryClient
        .get<{ data: TPopularDestination[] }>(
          'api/v1/looker/popular-destinations'
        )
        .then((response) => response.data.data);

      const sdk = getSdk(hygraphClient(preview));
      const { regions } = await sdk.Regions({
        filters: {
          // where has image
          image: {
            id_not: null,
          },
        },
      });

      // Combine the popularDestinations with Hygraph Region
      const popularDestinationResponses: PopularDestinationsResponse = [];
      popularDestinations.forEach((stats) => {
        const region = regions.find((r) => r.abbreviation === stats.state);

        if (region) {
          popularDestinationResponses.push({
            stats,
            region: {
              id: region.id,
              abbreviation: region.abbreviation,
              title: region.title,
              description: region.description,
              image: region.image,
              link: region.link,
            } as Region,
          });
        }
      });

      if (limit) {
        return popularDestinationResponses.slice(0, limit);
      }

      return popularDestinationResponses;
    },
    endOfDay(new Date())
  );
};

const get = async (page: Page): Promise<PopularDestinationsResponse> => {
  const hasPopularDestinationsCheck = (rc: RowrowComponentsUnion) =>
    rc.__typename === 'StaticComponent' &&
    rc.staticComponent === StaticComponentOptions.PopularDestinations;
  const hasPopularDestinations =
    getPageComponents(page, hasPopularDestinationsCheck).length > 0;

  return hasPopularDestinations
    ? await getPopularDestinations({
        limit: 8,
      })
    : [];
};

const PopularDestinationsService = {
  get,
};

export default PopularDestinationsService;
